import { TRANSFER_STATES } from '../constant/transfer-states';
import {
  MAX_MESSAGE_SIZE_IN_BYTES,
  REGEXP_BLANK,
} from '../config/transfer-config';

import Helper from '@app/helper';
import TransferError from '@app/helper/error';
import TransferErrorMessages from '@app/helper/error/messages';
import { UNLIMITED_TRANSFER_LIMIT } from '@app/constant/sizes';

export function validateTransfer({
  transfer,
  maximumNumberOfRecipients,
  maximumNumberOfFiles,
  allowedSize,
  currentSize,
}) {
  // Make sure this Transfer only starts once
  if (hasStarted(transfer.state, transfer.id)) {
    throw new TransferError('AlreadyStarted');
  }
  if (transfer.type !== 'email' && transfer.type !== 'link') {
    throw new TransferError('InvalidTransferType');
  }

  if (!transfer.files.length) {
    throw new TransferError('MissingFiles');
  }

  // For e-mail transfers also make sure we have recipients and sender
  if (transfer.type === 'email') {
    if (!transfer.recipients.length) {
      throw new TransferError('MissingRecipients');
    }
    if (!transfer.sender.replace(REGEXP_BLANK, '')) {
      throw new TransferError('MissingSender');
    }
    if (!Helper.validateEmail(transfer.sender)) {
      throw new TransferError('InvalidSender');
    }

    // Make sure the recipients are valid too.
    const invalidRecipients = transfer.recipients.filter(
      (email) => !Helper.validateEmail(email)
    );
    if (invalidRecipients.length) {
      throw new TransferError('InvalidRecipient');
    }

    // We only allow so many recipients for a transfer
    if (transfer.recipients.length > maximumNumberOfRecipients) {
      throw new TransferError(TransferErrorMessages.TOO_MANY_RECIPIENTS);
    }
  }

  // For messages we have a max. size (it's pretty high though)
  const messageSize = unescape(encodeURIComponent(transfer.message)).length;
  if (messageSize > MAX_MESSAGE_SIZE_IN_BYTES) {
    throw new TransferError('MessageTooLarge');
  }

  // If the total transfer size is higher than our limit, notify the user
  if (!isTransferSizeUnlimited(allowedSize) && currentSize > allowedSize) {
    throw new TransferError('TransferTooBig');
  }

  // Does it have more files than we allow?
  if (transfer.files.length > maximumNumberOfFiles) {
    throw new TransferError('TooManyFiles');
  }
}

export function isTransferSizeUnlimited(allowedSize) {
  return allowedSize === UNLIMITED_TRANSFER_LIMIT;
}

export function hasStarted(transferState, transferId) {
  return transferState !== TRANSFER_STATES.INIT || Boolean(transferId);
}

export function canFilesBeAdded({
  currentSize,
  files,
  allowedSize,
  isForwardedTransfer,
}) {
  // For unlimited size transfers we don't have to calculate sizes
  if (isTransferSizeUnlimited(allowedSize)) {
    return true;
  }

  // If the transfer is being forwarded, we don't have to calculate sizes
  if (isForwardedTransfer) {
    return true;
  }

  const bytesToAdd = files.reduce((count, file) => file.size + count, 0);
  const transferSizeWithFiles = currentSize + bytesToAdd;
  return transferSizeWithFiles < allowedSize;
}
